import { Directive, ElementRef, OnDestroy, OnInit, Renderer2 } from '@angular/core';
import { NgSelectComponent } from '@ng-select/ng-select';
import { TranslateService } from '@ngx-translate/core';
import { Subscription } from 'rxjs';

/**
 * Sagt Screenreader-Nutzern beim Tippen in ein ng-select, wie viele Einträge noch
 * passen. ng-select selbst meldet nur den leeren Treffer, und das sofort beim
 * Tastendruck, wo JAWS die Meldung hinter dem Zeichen-Echo verwirft.
 *
 * Deshalb wird erst nach einer kurzen Tipppause gemeldet, einmal je Pause.
 * Zwei abwechselnd beschriebene Statusbereiche sorgen dafür, dass auch ein
 * gleichlautender Text erneut vorgelesen wird (Muster aus gov.uk
 * accessible-autocomplete).
 */
@Directive({
    selector: 'ng-select[otbAnnounceSearchResults]',
    standalone: true,
})
export class AnnounceSearchResultsDirective implements OnInit, OnDestroy {
    // 600 ms waren zu knapp: JAWS las die Ansage noch nicht zu Ende, bevor die
    // nächste Taste sie verwarf. gov.uk nutzt 1400 ms.
    static readonly PAUSE_MS = 1000;

    private readonly subs = new Subscription();
    private container!: HTMLElement;
    private regions: HTMLElement[] = [];
    private nextRegion = 0;
    private pending: ReturnType<typeof setTimeout> | null = null;

    constructor(
        private ngSelect: NgSelectComponent,
        private el: ElementRef<HTMLElement>,
        private renderer: Renderer2,
        private translate: TranslateService,
    ) {}

    ngOnInit(): void {
        this.container = this.renderer.createElement('div');
        this.renderer.addClass(this.container, 'visually-hidden');
        this.renderer.addClass(this.container, 'otb-search-results-status');
        this.regions = [this.createRegion(), this.createRegion()];

        const host = this.el.nativeElement;
        this.renderer.insertBefore(host.parentNode, this.container, host.nextSibling);

        this.subs.add(
            this.ngSelect.searchEvent.subscribe(({ term, items }: { term: string; items: unknown[] }) =>
                this.schedule(term, items.length),
            ),
        );
        this.subs.add(this.ngSelect.closeEvent.subscribe(() => this.reset()));
        this.subs.add(this.ngSelect.clearEvent.subscribe(() => this.reset()));
    }

    ngOnDestroy(): void {
        this.cancel();
        this.subs.unsubscribe();
        this.container?.remove();
    }

    private createRegion(): HTMLElement {
        const region = this.renderer.createElement('div');
        this.renderer.setAttribute(region, 'role', 'status');
        this.renderer.setAttribute(region, 'aria-live', 'polite');
        this.renderer.setAttribute(region, 'aria-atomic', 'true');
        this.renderer.appendChild(this.container, region);

        return region;
    }

    private schedule(term: string, count: number): void {
        this.cancel();
        if (term === '') {
            this.clearRegions();
            return;
        }
        this.pending = setTimeout(() => {
            this.pending = null;
            this.announce(this.message(count));
        }, AnnounceSearchResultsDirective.PAUSE_MS);
    }

    private announce(text: string): void {
        this.clearRegions();
        this.regions[this.nextRegion].textContent = text;
        this.nextRegion = (this.nextRegion + 1) % this.regions.length;
    }

    private message(count: number): string {
        const key =
            count === 0
                ? 'dynamische_frontend_texte.landing.attribute_text.suche_keine_treffer'
                : count === 1
                  ? 'dynamische_frontend_texte.landing.attribute_text.suche_ein_treffer'
                  : 'dynamische_frontend_texte.landing.attribute_text.suche_treffer';

        return this.translate.instant(key).replace('%count%', String(count));
    }

    private reset(): void {
        this.cancel();
        this.clearRegions();
    }

    private cancel(): void {
        if (this.pending !== null) {
            clearTimeout(this.pending);
            this.pending = null;
        }
    }

    private clearRegions(): void {
        this.regions.forEach((region) => (region.textContent = ''));
    }
}
